import { useState } from 'react';
import type { MediaAsset } from '@inboundhost/shared';
import { useDeleteMedia, useMediaList, useUploadMedia } from '../../hooks/useMedia';
import { imagePreviewUrl } from '../../lib/images';

interface Props {
  siteId: string;
  onSelect: (asset: MediaAsset) => void;
}

/**
 * Reusable media library browser: upload a new image or pick an existing one.
 * Calls `onSelect` with the chosen asset.
 */
export function MediaPicker({ siteId, onSelect }: Props) {
  const list = useMediaList(siteId);
  const uploadMedia = useUploadMedia(siteId);
  const deleteMedia = useDeleteMedia(siteId);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(file: File) {
    setError(null);
    try {
      const { media } = await uploadMedia.mutateAsync(file);
      onSelect(media);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  const media = list.data?.media ?? [];

  return (
    <div className="border rounded p-4 bg-gray-50">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-gray-700">Media library</span>
        <label>
          <span className="px-3 py-1 border rounded text-sm bg-white hover:bg-gray-100 cursor-pointer">
            {uploadMedia.isPending ? 'Uploading…' : '+ Upload'}
          </span>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            disabled={uploadMedia.isPending}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFile(file);
              e.target.value = '';
            }}
          />
        </label>
      </div>

      {error && <div className="text-sm text-red-600 mb-2">{error}</div>}

      {list.isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : media.length === 0 ? (
        <p className="text-sm text-gray-500">No images yet. Upload one to get started.</p>
      ) : (
        <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
          {media.map((asset) => (
            <div key={asset.id} className="relative group">
              <button
                type="button"
                className="block w-full border rounded overflow-hidden hover:ring-2 hover:ring-gray-900"
                onClick={() => onSelect(asset)}
                title={asset.filename ?? asset.key}
              >
                <img
                  src={imagePreviewUrl(asset.key, { width: 200, height: 150, fit: 'cover' })}
                  alt={asset.filename ?? ''}
                  className="w-full h-24 object-cover"
                />
              </button>
              <button
                type="button"
                className="absolute top-1 right-1 bg-white/90 text-red-600 text-xs px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100"
                onClick={() => deleteMedia.mutate(asset.id)}
                title="Delete"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
